import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSurveyStore } from '../../helpers/ecxsurvey/surveyutilities/surveyStore';
import surveyService from '../../../services/survey.service';
import Loading from '../../common/Loading';
import SurveyPreviewModal from "./preview/SurveyPreviewModal";
// Create Survey Page - Template Selection
function SurveyTemplate() {
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();
    const { resetStore,loadSurveyData } = useSurveyStore();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [previewTemplate, setPreviewTemplate] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    // Fetch templates on mount
    useEffect(() => {
        setLoading(true);
        surveyService.getSurveyTemplates()
            .then((res) => {
                if (Array.isArray(res)) {
                    setTemplates(res);
                } else if (res?.data && Array.isArray(res.data)) {
                    setTemplates(res.data);
                } else {
                    console.warn('Unexpected response format:', res);
                }
                setLoading(false);
            })
            .catch((error) => {
                setLoading(false);
                console.error('❌ Error fetching surveys:', error);
            });
    }, []);
    const onBack = () => {
        navigate('/surveys');
    }
    // Start a blank survey
    const handleOnStartBlank = () => {
        resetStore();
        navigate('/surveys/create-survey');
    };

    // Select a template
    const handleOnSelectTemplate = (surveyId) => {
        // Find selected template
        const selectedTemplate = templates.find(t => t.survey?.id === surveyId);
        if (!selectedTemplate) {
            console.warn('Template not found:', surveyId);
            return;
        }
        // Reset store first
        resetStore();
        // Load survey data into store
        loadSurveyData(selectedTemplate);
        // Navigate to survey editor
        navigate('/surveys/create-survey');
    };
    const handlePreview = (template) => {
        setPreviewTemplate(template);
        setShowPreview(true);
    };

    // Generate unique categories
    const categories = ['all', ...new Set(templates.map(t => t.survey?.category || 'Uncategorized'))];

    // Filter templates based on category & search
    const filteredTemplates = templates.filter(template => {
        const title = template.survey?.title || '';
        const description = template.survey?.description || '';
        const category = template.survey?.category || 'Uncategorized';

        const matchesCategory = selectedCategory === 'all' || category === selectedCategory;
        const matchesSearch = title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            description.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    return (
        <div className="min-vh-100 bg-light">
            <div className="app-container container-xxl">

                {/* Back Button and Header */}
                <button className="btn btn-link text-decoration-none mb-3 ps-0" onClick={onBack}>
                    <i className="bi bi-arrow-left me-2"></i>Back to Surveys
                </button>

                <div className="text-center mb-5">
                    <h2 className="fw-bold mb-2">Create New Survey</h2>
                    <p className="text-muted">Choose a template to get started or create from scratch</p>
                </div>

                {/* Start from Blank Card */}
                <div className="row justify-content-center mb-4">
                    <div className="col-lg-8">
                        <div className="card border-0 shadow-sm bg-gradient" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                            <div className="card-body p-4">
                                <div className="row align-items-center">
                                    <div className="col-md-8">
                                        <div className="d-flex align-items-center mb-2">
                                            <div className="bg-white bg-opacity-25 rounded p-2 me-3">
                                                <i className="bi bi-file-earmark-plus text-white fs-2"></i>
                                            </div>
                                            <div>
                                                <h4 className="mb-1 fw-bold">Start from Blank</h4>
                                                <p className="text-white-75 mb-0">
                                                    Create a custom survey from scratch with complete control
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-4 text-md-end mt-3 mt-md-0">
                                        <button
                                            className="btn btn-primary btn-lg text-white fw-semibold"
                                            onClick={handleOnStartBlank}
                                        >
                                            <i className="bi bi-plus-circle me-2"></i>Create Blank Survey
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Divider */}
                <div className="row justify-content-center mb-4">
                    <div className="col-lg-12">
                        <div className="d-flex align-items-center">
                            <hr className="flex-grow-1" />
                            <span className="px-3 text-muted fw-semibold">OR CHOOSE A TEMPLATE</span>
                            <hr className="flex-grow-1" />
                        </div>
                    </div>
                </div>

                {/* Search and Filter */}
                <div className="row justify-content-center mb-4">
                    <div className="col-lg-12">
                        <div className="container-fluid">
                            <div className="row g-3">
                                <div className="col-md-7">
                                    <div className="input-group input-group-lg">
                    <span className="input-group-text bg-white border-end-0">
                      <i className="bi bi-search text-muted"></i>
                    </span>
                                        <input
                                            type="text"
                                            className="form-control border-start-0 form-control-lg"
                                            placeholder="Search templates..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-5">
                                    <select
                                        className="form-select form-select-lg"
                                        value={selectedCategory}
                                        onChange={(e) => setSelectedCategory(e.target.value)}
                                    >
                                        {categories.map(category => (
                                            <option key={category} value={category}>
                                                {category === 'all' ? 'All Categories' : category}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {loading && (
                    <div className="row justify-content-center">
                        <Loading />
                    </div>
                )}

                {/* Template Count */}
                <div className="row justify-content-center mb-3">
                    <div className="col-lg-12">
                        <p className="text-muted mb-0">
                            <i className="bi bi-funnel me-2"></i>
                            Showing {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''}
                            {selectedCategory !== 'all' && ` in ${selectedCategory}`}
                        </p>
                    </div>
                </div>

                {/* Templates Grid */}
                <div className="row justify-content-center">
                    <div className="col-lg-12">
                        <div className="row g-4">
                            {filteredTemplates.map(template => (
                                <div key={template.survey?.id || Math.random()} className="col-md-4">
                                    <div className="card border-0 shadow-sm h-100 hover-lift">
                                        <div className="card-body d-flex flex-column">
                                            <div className="d-flex align-items-start mb-3">
                                                {/* Icon */}
                                                <div className={`bg-${template.survey?.iconColor || 'primary'} bg-opacity-10 rounded p-3 me-3`}>
                                                    <i className={`bi bi-${template.survey?.icon || 'file-earmark'} text-${template.survey?.iconColor || 'primary'} fs-3`}></i>
                                                </div>

                                                {/* Survey Title & Category */}
                                                <div className="flex-grow-1">
                                                    <h5 className="card-title fw-bold mb-1">{template.survey?.title || "Untitled Survey"}</h5>
                                                    <span className="badge bg-light text-dark small">
                            {template.survey?.category || "Uncategorized"}
                          </span>
                                                </div>
                                            </div>

                                            {/* Description */}
                                            <p className="card-text text-muted mb-3 flex-grow-1">
                                                {template.survey?.description || "No description provided."}
                                            </p>

                                            {/* Questions and Estimated Time */}
                                            <div className="d-flex align-items-center justify-content-between mb-3">
                        <span className="text-muted small">
                          <i className="bi bi-question-circle me-1"></i>
                            {template.questions?.length ?? 0} question{(template.questions?.length ?? 0) !== 1 ? 's' : ''}
                        </span>
                                                <span className="text-muted small">
                          <i className="bi bi-clock me-1"></i>
                          ~{Math.ceil((template.questions?.length ?? 0) / 2)} min
                        </span>
                                            </div>

                                            {/* Action Buttons */}
                                            <div className="d-grid gap-2">
                                                <button
                                                    className="btn btn-primary text-white"
                                                    onClick={() => handleOnSelectTemplate(template.survey?.id)}
                                                >
                                                    <i className="bi bi-check-circle me-2"></i>Use This Template
                                                </button>
                                                <button className="btn btn-outline-secondary btn-sm" onClick={() => handlePreview(template)}>
                                                    <i className="bi bi-eye me-2"></i>Preview Questions
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {filteredTemplates.length === 0 && (
                                <div className="text-center py-5">
                                    <i className="bi bi-inbox text-muted" style={{ fontSize: '4rem' }}></i>
                                    <h4 className="text-muted mt-3">No templates found</h4>
                                    <p className="text-muted">Try adjusting your search or filter</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {previewTemplate && (
                <SurveyPreviewModal
                    show={showPreview}
                    onClose={() => setShowPreview(false)}
                    survey={previewTemplate.survey}
                    questions={previewTemplate.questions}
                />
            )}

            <style>{`
        .hover-lift {
          transition: transform 0.2s, box-shadow 0.2s;
        }
        .hover-lift:hover {
          transform: translateY(-4px);
          box-shadow: 0 0.5rem 1.5rem rgba(0,0,0,0.15) !important;
        }
      `}</style>
        </div>
    );
}

export default SurveyTemplate;
